import { Input } from '@/components/atoms/Form/Input';
import { Switch } from '@/components/atoms/Switch';
import { computeText } from '@/locales/utils';
import { formatDate } from '@/utils/date';
import { useEffect, useState } from 'react';

import {
    LoyaltyConfig,
    LoyaltyProgram,
    AuthenticationMethods,
    RegisterMethods,
    LoyaltyFeature,
} from '@/services/innovorder/modules/modules.type';
import React from 'react';
import { Control, Form, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Select from '@/components/atoms/Select';
import { useGetBrandOptInsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useParams } from 'react-router-dom';
import { useGetSplioSubscriptionsQuery } from '@/services/innovorder/modules/modules.endpoints';
import { FormWrapper } from './SplioConfigUpdateForm.style';
import { SwitchGroup } from '@/components/atoms/SwitchGroup';
import LoyaltyFeatures from '../../Loyalty/LoyaltyFeatures';

export interface UpdateSplioConfigForm {
    programId: string;
    allowCustomerRewardUsage: boolean;
    optIn: number | null;
    memberIdentifier: string;
    subscriptionToMarketingNewsletter: number | null;
    subscriptionToLoyaltyNewsletter: number | null;
    formLink: string;
    useCasualTone: boolean;
    authenticationMethods: AuthenticationMethods;
    registerMethods: RegisterMethods;
    features: LoyaltyFeature[];
}

const SplioConfigUpdateForm = ({
    control,
    loyaltyPrograms,
    loyaltyConfig,
}: {
    control: Control<UpdateSplioConfigForm>;
    loyaltyPrograms?: LoyaltyProgram[];
    loyaltyConfig?: LoyaltyConfig;
}) => {
    const intl = useIntl();
    const { brandId, restaurantId } = useParams<{ brandId: string; restaurantId: string }>();
    const { data: brandOptIns } = useGetBrandOptInsQuery({ brandId: Number(brandId) });
    const { data: newsletterOptions } = useGetSplioSubscriptionsQuery({ restaurantId: Number(restaurantId) });

    return (
        <Form control={control}>
            <FormWrapper>
                <p>
                    {computeText(intl, 'splio.integrationEnabled', {
                        date: loyaltyConfig?.createdAt
                            ? formatDate(loyaltyConfig.createdAt, 'DD/MM/YYYY HH:mm:ss')
                            : '',
                    })}
                </p>
                <Controller
                    name="allowCustomerRewardUsage"
                    control={control}
                    render={({ field: { onChange, value, ...otherProps } }) => (
                        <Switch
                            {...otherProps}
                            labelId="splio.allowCustomerRewardUsage"
                            onChange={() => onChange(!value)}
                            value={value}
                        />
                    )}
                    defaultValue={loyaltyConfig?.allowCustomerRewardUsage}
                />

                {loyaltyConfig && (
                    <Controller
                        name="programId"
                        control={control}
                        render={({ field: { onChange, value, ...otherProps } }) => (
                            <Select
                                {...otherProps}
                                labelId="splio.programId"
                                options={
                                    loyaltyPrograms?.map((form) => ({
                                        label: form.name,
                                        value: form.id.toString(),
                                    })) ?? []
                                }
                                onChange={onChange}
                                value={value}
                            />
                        )}
                        defaultValue={loyaltyConfig?.programId}
                    />
                )}

                <Controller
                    name="memberIdentifier"
                    control={control}
                    render={({ field: { onChange, value, ...otherProps } }) => (
                        <Input
                            {...otherProps}
                            labelId="splio.memberIdentifier"
                            onChange={onChange}
                            value={value}
                            disabled
                        />
                    )}
                    defaultValue={loyaltyConfig?.memberIdentifier}
                />
                {newsletterOptions && (
                    <Controller
                        name="subscriptionToMarketingNewsletter"
                        control={control}
                        render={({ field: { onChange, value, ...otherProps } }) => (
                            <Select
                                {...otherProps}
                                labelId="splio.subscriptionToMarketingNewsletter"
                                options={[{ name: '-', id: null }, ...newsletterOptions].map((option) => ({
                                    label: option.name,
                                    value: option.id as number,
                                }))}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                        defaultValue={loyaltyConfig?.optInLists.MARKETING ?? null}
                    />
                )}
                {brandOptIns && (
                    <Controller
                        name="optIn"
                        control={control}
                        render={({ field: { onChange, value, ...otherProps } }) => (
                            <Select
                                {...otherProps}
                                labelId="splio.optIn"
                                options={[{ text: '-', optInId: null }, ...brandOptIns].map((optIn) => ({
                                    label: optIn.text,
                                    value: optIn.optInId as number,
                                }))}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                        defaultValue={
                            loyaltyConfig?.optIns?.find((x) => x.optInType === 'MARKETING')?.optInId ?? undefined
                        }
                    />
                )}
                {newsletterOptions && (
                    <Controller
                        name="subscriptionToLoyaltyNewsletter"
                        control={control}
                        render={({ field: { onChange, value, ...otherProps } }) => (
                            <Select
                                {...otherProps}
                                labelId="splio.subscriptionToLoyaltyNewsletter"
                                options={[{ name: '-', id: null }, ...newsletterOptions].map((option) => ({
                                    label: option.name,
                                    value: option.id as number,
                                }))}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                        defaultValue={loyaltyConfig?.optInLists.LOYALTY ?? null}
                    />
                )}
                {loyaltyConfig && (
                    <Controller
                        name="formLink"
                        control={control}
                        rules={{
                            validate: (value) => {
                                try {
                                    if (!value) {
                                        return true;
                                    }
                                    const url = new URL(value);
                                    return (
                                        url.protocol === 'http:' ||
                                        url.protocol === 'https:' ||
                                        computeText(intl, 'splio.error.formLinkInvalid')
                                    );
                                } catch (e) {
                                    return computeText(intl, 'splio.error.formLinkInvalid');
                                }
                            },
                        }}
                        render={({ field: { onChange, value, ...otherProps }, formState: { errors } }) => (
                            <Input
                                labelId="splio.formLink"
                                {...otherProps}
                                value={value}
                                onChange={onChange}
                                error={errors.formLink?.message}
                            />
                        )}
                        defaultValue={loyaltyConfig?.formLink}
                    />
                )}
                <Controller
                    name="useCasualTone"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Switch labelId="splio.useCasualTone" onChange={() => onChange(!value)} value={value} />
                    )}
                    defaultValue={loyaltyConfig?.useCasualTone}
                />

                <Controller
                    name="authenticationMethods"
                    control={control}
                    rules={{
                        validate: (value) => value.email || value.phone || value.externalId,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <SwitchGroup
                            labelId="splio.authenticationMethods"
                            value={value}
                            onChange={onChange}
                            options={[
                                { labelId: 'splio.methods.email', name: 'email', value: value.email },
                                { labelId: 'splio.methods.phone', name: 'phone', value: value.phone },
                                { labelId: 'splio.methods.externalId', name: 'externalId', value: value.externalId },
                            ]}
                        />
                    )}
                    defaultValue={loyaltyConfig?.authenticationMethods}
                />

                <Controller
                    name="registerMethods"
                    control={control}
                    rules={{
                        validate: (value) => !(value.email && value.phone),
                    }}
                    render={({ field: { onChange, value } }) => (
                        <SwitchGroup
                            labelId="splio.registerMethods"
                            value={value}
                            onChange={onChange}
                            options={[
                                { labelId: 'splio.methods.email', name: 'email', value: value.email, disabled: true },
                                { labelId: 'splio.methods.phone', name: 'phone', value: value.phone, disabled: true },
                            ]}
                            isUnique
                        />
                    )}
                    defaultValue={loyaltyConfig?.registerMethods}
                />

                <Controller
                    name="features"
                    control={control}
                    defaultValue={loyaltyConfig?.features}
                    render={({ field: { onChange, value } }) => (
                        <LoyaltyFeatures features={value} onChange={onChange} />
                    )}
                />
            </FormWrapper>
        </Form>
    );
};

export default SplioConfigUpdateForm;
